import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Flame, Camera, TrendingUp, MapPin, WifiOff, Swords, Crown, Layers,
  ChevronRight, Compass, Sparkles,
} from 'lucide-react';
import api from '../lib/api';
import { cacheObservations, getCachedObservations } from '../lib/offlineCache';
import CinematicBackground from '../components/CinematicBackground';
import { useT } from '../i18n/I18nContext';
import {
  SeasonalBanner, LiveCommunityFeed, WorldFirstCard,
  PlatformStats, FeatureHighlights, PremiumCta,
} from '../components/LandingBlocks';

const RANK_COLORS = { Bronze: '#B08B58', Silver: '#C0C0C0', Gold: '#E9C46A', Platinum: '#8ECAE6', Diamond: '#FF6EC7' };

export default function Dashboard() {
  const nav = useNavigate();
  const t = useT();
  const [data, setData] = useState(null);
  const [battleStats, setBattleStats] = useState(null);
  const [cardCount, setCardCount] = useState(null);
  const [wildcoins, setWildcoins] = useState(null);
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(() => {});
    api.get('/observations/feed?limit=8').then(r => cacheObservations(r.data)).catch(async () => {
      const cached = await getCachedObservations(); if (cached.length) {/* keep for offline */}
    });
    api.get('/battle-stats/me').then(r => setBattleStats(r.data)).catch(() => {});
    api.get('/cards?limit=1').then(r => setCardCount((r.data || []).length)).catch(() => setCardCount(0));
    api.get('/coins/balance').then(r => setWildcoins(r.data.wildcoins)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="animate-pulse space-y-4" data-testid="dashboard-loading">
        <div className="h-56 bg-card rounded-3xl" />
        <div className="h-32 bg-card rounded-3xl" />
      </div>
    );
  }

  const q = data.daily_quests?.[0];
  const level = data.level;
  const streak = data.user.streak || 0;
  const species = data.user.species_count || 0;

  return (
    <div className="relative -mx-5 sm:mx-0" data-testid="dashboard-root">
      {/* ===== CINEMATIC HERO ===== */}
      <section className="relative overflow-hidden rounded-none sm:rounded-3xl">
        <div className="relative h-[62svh] min-h-[440px] max-h-[560px] wq-hero-poster">
          <CinematicBackground intensity="full" />

          {/* Foreground content */}
          <div className="relative z-10 h-full flex flex-col justify-between p-6">
            {/* Top: greeting + minimal status */}
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="flex items-start justify-between"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-primary-light">{t('dashboard.welcomeBack', 'Welcome back')}</p>
                <p className="font-serif text-2xl mt-1 leading-tight">{data.user.name}</p>
              </div>
              <div className="text-right">
                <MinimalPlayerStatus level={level} streak={streak} species={species} coins={wildcoins} t={t} />
              </div>
            </motion.div>

            {/* Center CTA — Begin Exploring */}
            <div className="flex flex-col items-center gap-3 pb-2">
              <motion.button
                data-testid="btn-begin-exploring"
                onClick={() => nav('/discover')}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.7 }}
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                className="wq-hero-cta relative w-52 h-52 rounded-full text-white font-serif text-xl
                           bg-gradient-to-br from-primary via-primary-light to-primary
                           border-4 border-white/10 shadow-2xl shadow-primary/40
                           flex flex-col items-center justify-center gap-1.5"
              >
                <motion.span
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(233,196,106,0.35), transparent 60%)', filter: 'blur(28px)' }}
                  animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 3, repeat: Infinity }}
                />
                <Compass className="w-10 h-10 relative" strokeWidth={1.4} />
                <span className="relative">{t('dashboard.discoverWildlife', 'Discover Wildlife')}</span>
                <span className="wq-hero-cta-sub relative text-[10px] uppercase tracking-[0.25em] opacity-80">{t('dashboard.tapToBegin', 'Tap to begin')}</span>
              </motion.button>
              {q && !q.completed && (
                <motion.button
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
                  onClick={() => nav('/quests')}
                  data-testid="hero-active-quest"
                  className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                >
                  <Sparkles className="w-3 h-3 text-accent" />
                  {t('dashboard.activeQuest', 'Active quest:')} <span className="text-foreground">{q.title}</span>
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BELOW-HERO CONTENT ===== */}
      <div className="px-5 sm:px-0 pt-4 pb-6 space-y-6 relative">
        {offline && (
          <div className="rounded-2xl bg-amber-500/10 border border-amber-400/30 px-3 py-2 flex items-center gap-2 text-xs text-amber-200" data-testid="offline-banner">
            <WifiOff className="w-3.5 h-3.5" />
            {t('dashboard.offlineBanner', "You're offline — showing cached content. Live actions will resume once you're back online.")}
          </div>
        )}

        <LiveCommunityFeed />

        {q && <TodaysExpedition quest={q} onStart={() => nav('/quests')} t={t} />}

        <SeasonalBanner />

        <BattleArenaCard
          stats={battleStats}
          cardCount={cardCount}
          onEnter={() => nav('/battle')}
          onGoDiscover={() => nav('/discover')}
          t={t}
        />

        <PlatformStats />

        {/* Biodiversity Network entry point */}
        <div className="p-4 rounded-3xl glass border border-white/10 flex items-center gap-3" data-testid="biodiversity-network-card">
          <div className="w-11 h-11 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Compass className="w-5 h-5 text-primary-light" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-serif text-lg leading-tight">{t('dashboard.biodiversityNetwork', 'Biodiversity Network')}</p>
            <p className="text-[11px] text-white/60">{t('dashboard.biodiversityBody', 'Explore verified observations on the world map.')}</p>
          </div>
          <button
            data-testid="btn-open-biodiversity"
            onClick={() => nav('/biodiversity')}
            className="px-3 py-1.5 rounded-full bg-primary text-white text-xs"
          >{t('dashboard.open', 'Open')}</button>
        </div>

        <div className="p-4 rounded-3xl glass border border-white/10 flex items-center gap-3" data-testid="my-observations-card">
          <div className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary-light" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-serif text-lg leading-tight">{t('dashboard.myObservations', 'My Observations')}</p>
            <p className="text-[11px] text-white/60">{t('dashboard.myObservationsBody', 'Manage privacy, verification status & audit trail.')}</p>
          </div>
          <button
            data-testid="btn-open-observations"
            onClick={() => nav('/observations')}
            className="px-3 py-1.5 rounded-full glass border border-white/10 text-xs"
          >{t('dashboard.view', 'View')}</button>
        </div>

        <div className="p-4 rounded-3xl glass border border-white/10 flex items-center gap-3" data-testid="organizations-card">
          <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center">
            <Compass className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-serif text-lg leading-tight">{t('dashboard.researchOrgs', 'Research Organizations')}</p>
            <p className="text-[11px] text-white/60">{t('dashboard.researchOrgsBody', 'Universities, NGOs & parks turning sightings into science.')}</p>
          </div>
          <button
            data-testid="btn-open-organizations"
            onClick={() => nav('/organizations')}
            className="px-3 py-1.5 rounded-full glass border border-white/10 text-xs"
          >{t('dashboard.open', 'Open')}</button>
        </div>

        <div className="p-4 rounded-3xl glass border border-rose-400/30 flex items-center gap-3 bg-gradient-to-br from-rose-950/30 to-transparent" data-testid="world-bosses-card">
          <div className="w-11 h-11 rounded-2xl bg-rose-500/15 border border-rose-400/40 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-rose-300" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-serif text-lg leading-tight">{t('dashboard.worldBosses', 'World Bosses')}</p>
            <p className="text-[11px] text-white/60">{t('dashboard.worldBossesBody', 'Guild raids. Cooperate, deal damage, earn tiered rewards.')}</p>
          </div>
          <button
            data-testid="btn-open-world-bosses"
            onClick={() => nav('/world-bosses')}
            className="px-3 py-1.5 rounded-full bg-rose-500 text-white text-xs"
          >{t('dashboard.raid', 'Raid')}</button>
        </div>

        <div>
          <h3 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{t('dashboard.pillars', 'Pillars of the Quest')}</h3>
          <FeatureHighlights />
        </div>

        <WorldFirstCard />

        <PremiumCta />
      </div>
    </div>
  );
}

// ---------- Minimal player status (hero corner) ----------
function MinimalPlayerStatus({ level, streak, species, coins, t }) {
  return (
    <div className="flex items-center gap-3" data-testid="minimal-player-status">
      <StatusPill icon={Crown} label={t('dashboard.level', 'Lvl')} value={level} testid="hero-level" accent="#E9C46A" />
      <StatusPill icon={Flame} label={t('dashboard.streak', 'Streak')} value={`${streak}d`} testid="hero-streak" accent="#F97544" />
      <StatusPill icon={MapPin} label={t('dashboard.species', 'Species')} value={species} testid="hero-species" />
    </div>
  );
}

function StatusPill({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/45 backdrop-blur-md border border-white/10" data-testid={testid}>
      <Icon className="w-3.5 h-3.5" style={{ color: accent || '#9CA3AF' }} strokeWidth={1.75} />
      <div className="text-right">
        <p className="font-mono text-xs tabular-nums" style={{ color: accent || '#F9FAFB' }}>{value}</p>
        <p className="text-[8px] uppercase tracking-widest text-muted-foreground leading-none">{label}</p>
      </div>
    </div>
  );
}

// ---------- Today's Expedition ----------
function TodaysExpedition({ quest, onStart, t }) {
  const pct = Math.min(100, Math.round((quest.progress / quest.target) * 100));
  return (
    <motion.div
      data-testid="daily-quest"
      initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/30 via-primary/10 to-transparent border border-emerald-400/25 p-5"
    >
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="relative flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 bg-emerald-400/15 border border-emerald-400/40 text-2xl">🌿</div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">{t('dashboard.todaysExpedition', "Today's Expedition")}</p>
          <p className="font-serif text-xl mt-0.5">{quest.title}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{quest.description}</p>
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300"
                initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="font-mono">{quest.progress}/{quest.target}</span>
              <span>{t('dashboard.reward', 'Reward')} · <span className="text-accent">{t('dashboard.xpReward', '+{xp} XP', { xp: quest.xp_reward })}</span></span>
            </div>
          </div>
        </div>
      </div>
      <button
        onClick={onStart}
        data-testid="btn-start-quest"
        className="mt-4 w-full py-3 rounded-full bg-emerald-400 text-black font-semibold uppercase tracking-widest text-xs"
      >
        {t('dashboard.startQuest', 'Start Quest')}
      </button>
    </motion.div>
  );
}

// ---------- ObsImage (shared, used by Profile & elsewhere) ----------
export function ObsImage({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/files/${path}`, { responseType: 'blob' }).then(r => {
      const u = URL.createObjectURL(r.data); if (alive) setUrl(u);
    }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [path]);
  if (!url) return <div className="w-full h-full bg-muted animate-pulse" />;
  return <img src={url} alt="" className="w-full h-full object-cover" />;
}

// ---------- Battle Arena Card ----------
function BattleArenaCard({ stats, cardCount, onEnter, onGoDiscover, t }) {
  const rankColor = RANK_COLORS[stats?.rank?.tier] || '#E9C46A';
  const hasCards = (cardCount ?? 0) > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a0f14] via-[#0f0a0d] to-[#0a0908] p-5"
      data-testid="dashboard-battle-card"
    >
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full blur-3xl opacity-25" style={{ background: rankColor }} />
      <div className="relative flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border-2"
             style={{ borderColor: rankColor, background: `${rankColor}18` }}>
          <Swords className="w-6 h-6" style={{ color: rankColor }} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: rankColor }}>{t('dashboard.battleArena', 'Battle Arena')}</p>
          <p className="font-serif text-xl mt-0.5">{t('dashboard.fightForCrown', 'Fight for the crown')}</p>
          <p className="text-xs text-muted-foreground truncate">
            {hasCards ? t('dashboard.battleWithCards', 'Deploy your Battle Cards against AI or other explorers.') : t('dashboard.battleNoCards', 'Discover wildlife to mint your first Battle Card.')}
          </p>
        </div>
      </div>

      {hasCards && stats && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-black/30 border border-white/5 p-2.5">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Crown className="w-3 h-3" /> {t('dashboard.currentRank', 'Current Rank')}</p>
            <p className="font-serif text-base mt-0.5" style={{ color: rankColor }} data-testid="dashboard-battle-rank">{stats.rank?.label || t('dashboard.unranked', 'Unranked')}</p>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/5 p-2.5">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{t('dashboard.battleRating', 'Battle Rating')}</p>
            <p className="font-mono text-base mt-0.5 tabular-nums" style={{ color: rankColor }} data-testid="dashboard-battle-rating">{stats.rating}</p>
          </div>
        </div>
      )}

      <button
        onClick={hasCards ? onEnter : onGoDiscover}
        data-testid={hasCards ? 'dashboard-enter-arena' : 'dashboard-go-discover'}
        className="mt-4 w-full py-3 rounded-full text-black font-semibold uppercase tracking-widest text-xs flex items-center justify-center gap-1.5"
        style={{ background: rankColor }}
      >
        {hasCards ? <><Swords className="w-3.5 h-3.5" /> {t('dashboard.enterArena', 'Enter Arena')}</> : <>{t('dashboard.discoverToUnlock', 'Discover to unlock')}</>}
      </button>
    </motion.div>
  );
}
